import { RecipientTypeEnum } from 'Modules/Communication/shared/models/Inbox'
import {
  FilterTypeEnum,
  IBuildingTypes,
  IFrequentRecipientsModel,
  IMeterItemModel,
  IObjectItemModel,
  IRecipientModel,
  IRecipientQueryModel,
  MessageTypeEnum,
  RecipientOpenState,
} from 'Modules/Communication/shared/models/Recipients'
import { ICommittee } from 'Modules/HousingAssociation/BoardAndCommittees/shared/models/Committees'
import { IClientAddress } from 'Modules/HousingAssociation/OurCompany/shared/models/Unit'
import { ICommonListModel, ICommonSystemState } from 'shared/models/Common'
import { ISortOrderEnum } from 'shared/models/List'
import { isNotNullOrUndefined } from 'shared/utils/types'
import { ISendEmailState, ISendSmsState } from '../shared/SendMessage'

export interface SystemState {
  sendMessage: {
    recipients: RecipientState
    // To handle logic regarding frequent users
    sms: ISendSmsState
    email: ISendEmailState
  }
}

export interface RecipientState {
  data: ICommonListModel<IRecipientModel> | null
  loading: boolean
  error: unknown
  filters: IRecipientQueryModel
  listedRecipients: IRecipientModel[]
  listedRecipientsRemovalQueue: IRecipientModel[]
  openState: RecipientOpenState
  openStateType: RecipientTypeEnum | null
  frequentRecipients: {
    sms: ICommonSystemState<IFrequentRecipientsModel[]>
    fetchedSmsWithClientId: string | null
    email: ICommonSystemState<IFrequentRecipientsModel[]>
    fetchedEmailWithClientId: string | null
  }
  clientAddresses: ICommonSystemState<IClientAddress[]>
  buildingTypes: ICommonSystemState<IBuildingTypes[]>
  committees: ICommonSystemState<ICommittee[]>
  meters: IMeterItemModel[]
  objects: IObjectItemModel[]
}

const _getBoardMemberRecipients = (state: RecipientState) => {
  const { skip, take, searchText, sortcolumn, sortorder } = state.filters

  const clone: RecipientState = Object.assign({}, state)

  const searched = clone.data?.result
    .filter((item) => {
      // Find if any object values contain searchText
      const v = Object.values(item).some((value) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchText?.toLowerCase() || '')
        }
        return false
      })

      if (v) return item
      return null
    })
    .filter(Boolean)

  const result = searched?.slice(skip, take) || []

  const sorted = result?.sort((a, b) => {
    if (!sortcolumn) return 0

    const val1 = a[sortcolumn as keyof IRecipientModel] as string
    const val2 = b[sortcolumn as keyof IRecipientModel] as string

    if (val1 === undefined || val2 === undefined) return -1

    // eslint-disable-next-line no-constant-binary-expression
    if (val1 === null || !val2 === null) return -1

    return val1.localeCompare(val2)
  })

  const data: ICommonListModel<IRecipientModel> = {
    currentPage: Math.floor(skip / take) + 1,
    morePages: false,
    resultCount: sorted?.length || 0,
    totalCount: sorted?.length || 0,
    totalPageCount: Math.ceil(sorted?.length / take) || 1,
    result: sortorder === ISortOrderEnum.asc ? sorted : sorted.reverse(),
  }

  clone.data = data
  return clone
}

const _getFiveMostFrequentRecipients = (
  state: SystemState,
  search: string,
  type: MessageTypeEnum,
  recipientType: RecipientTypeEnum
) => {
  const isSmsType = type === MessageTypeEnum.SMS
  const messageType = isSmsType ? 'sms' : 'email'

  const recipientState = getMessageRecipients(state)

  const frequentRecipients = recipientState.frequentRecipients

  const searched = frequentRecipients[messageType].data
    ?.filter((item) => {
      const v = Object.values(item).some((value) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(search?.toLowerCase() || '')
        }
        return false
      })

      if (v) return item
      return null
    })
    .filter(Boolean)

  const messageRecipients =
    state.sendMessage[messageType].message?.recipients || []

  // If message recipient has the same nameId, email or phone as frequent recipient remove it
  const filtered = searched?.filter((item) => {
    const recipientWithNameId = messageRecipients?.find((value) => {
      return (
        isNotNullOrUndefined(value.nameregisterId) &&
        isNotNullOrUndefined(item.nameregisterId) &&
        value.nameregisterId === item.nameregisterId &&
        value.recipientType === recipientType
      )
    })

    if (recipientWithNameId) {
      return false
    }

    const recipientWithSendValue = messageRecipients?.find(
      (value) =>
        value.recipientType === recipientType &&
        (isSmsType
          ? value.phonenumber === item.phonenumber
          : value.email?.toLowerCase() === item.email?.toLowerCase())
    )

    if (recipientWithSendValue) return false

    return true
  })

  // If we start to search, we should not sort out already chosen recipients
  const slicedEmail = search ? searched?.slice(0, 5) : filtered?.slice(0, 5)
  return slicedEmail || []
}

export const getMessageRecipients = (state: SystemState) => {
  if (
    state.sendMessage.recipients.filters.filterType ===
    FilterTypeEnum.BoardMembers
  ) {
    return _getBoardMemberRecipients(state.sendMessage.recipients)
  }

  return state.sendMessage.recipients
}

export const getListedRecipient = (nameId: string) => (state: SystemState) => {
  const listedRecipients = getMessageRecipients(state).listedRecipients
  return listedRecipients.find((recipient) => recipient.nameId === nameId)
}

export const getListedRecipients = (state: SystemState) =>
  getMessageRecipients(state).listedRecipients

export const getRecipientOpenState = (state: SystemState) =>
  getMessageRecipients(state).openState

export const getRecipientsClientAddresses = (state: SystemState) =>
  getMessageRecipients(state).clientAddresses

export const getRecipientsBuildingTypes = (state: SystemState) =>
  getMessageRecipients(state).buildingTypes

export const getRecipientsCommittees = (state: SystemState) =>
  getMessageRecipients(state).committees

export const getRecipientObjects = (state: SystemState) =>
  getMessageRecipients(state).objects

export const getRecipientMeters = (state: SystemState) =>
  getMessageRecipients(state).meters

export const getFrequentRecipients = (state: SystemState) =>
  getMessageRecipients(state).frequentRecipients

export const getListedRecipientsRemovalQueue = (state: SystemState) =>
  getMessageRecipients(state).listedRecipientsRemovalQueue

export const getFrequentRecipientsSearch =
  ({
    search = '',
    type = MessageTypeEnum.Email,
    recipientType,
  }: {
    search?: string
    type?: MessageTypeEnum
    recipientType: RecipientTypeEnum
  }) =>
  (state: SystemState) => {
    return _getFiveMostFrequentRecipients(state, search, type, recipientType)
  }
